import React, { useState } from 'react';
import styled from 'styled-components';
import { isEmpty, isNil } from 'lodash';
import { Form, Formik } from 'formik';
import { useTranslation } from '@lt/localization';
import type { FormValues } from './types';
import { FormFields } from './types';
import { Title } from '../Title';
import { Description } from '../Description';
import { Content } from '../Content';
import { ButtonWithLoader, Loader } from '../ButtonWithLoader';
import { ActionItem } from '../ActionItem';
import type { AuthConfig } from '../../types';
import { RequestStatus } from '../../types';
import { PasswordInput } from '../PasswordInput';
import { AuthSuccess } from '../AuthSuccess';

interface Props {
  email: string;
  onSubmit: (config: AuthConfig) => Promise<void>;
  onResetPassword: (email: string) => Promise<void>;
  requestStatus: RequestStatus;
}

const initialValues: FormValues = {
  password: '',
};

export const PasswordForm: React.FC<Props> = ({
  email,
  onSubmit,
  onResetPassword,
  requestStatus,
}) => {
  const { t } = useTranslation();
  const [isSecure, setIsSecure] = useState(true);
  const [isResetClicked, setIsResetClicked] = useState(false);

  const handleSubmit = async ({ password }: FormValues, { setErrors }) => {
    try {
      if (isNil(password)) {
        return;
      }

      setIsSecure(true);
      await onSubmit({ login: email, password });
    } catch (e) {
      console.dir(e);
      setErrors({ [FormFields.Password]: (e as Error)?.message ?? '' });
    }
  };

  const handleReset = async () => {
    try {
      if (!isResetClicked) {
        setIsResetClicked(true);
        await onResetPassword(email);
      }
    } catch (e) {
      console.dir(e);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur
    >
      {({ isSubmitting, errors, values, handleChange }) => (
        <Form>
          <Title>{t('checkout.passwordForm.title')}</Title>
          <Description>
            {t('checkout.passwordForm.description')} {email}
          </Description>
          <Content>
            <PasswordInput
              name={FormFields.Password}
              header={t('checkout.passwordForm.input')}
              placeholder={t('checkout.passwordForm.input')}
              isSecure={isSecure}
              handleChange={handleChange}
              postfixIconProps={{
                onClick: () => setIsSecure((secure) => !secure),
              }}
            />

            {requestStatus !== RequestStatus.Succeeded && (
              <>
                <ButtonWithLoader
                  size="large"
                  primary
                  type="submit"
                  id="user_authorize"
                  isLoading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    isEmpty(values.password) ||
                    !!errors.password
                  }
                  width="100%"
                >
                  {t('checkout.passwordForm.login')}
                </ButtonWithLoader>
                <StyledActionItem onClick={handleReset}>
                  {isResetClicked ? (
                    <StyledLoader _visible />
                  ) : (
                    t('checkout.passwordForm.unknown')
                  )}
                </StyledActionItem>
              </>
            )}
            {requestStatus === RequestStatus.Succeeded && <AuthSuccess />}
          </Content>
        </Form>
      )}
    </Formik>
  );
};

const StyledActionItem = styled(ActionItem)`
  margin: 18px 0 8px;
  position: relative;
  display: inline-flex;
`;

const StyledLoader = styled(Loader)`
  cursor: auto;
`;
